import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, Grid3x3, List, Plus, Search, MapPin, Video, Users, Clock, ArrowLeft, ArrowRight, Download, UserPlus, X, FileDown, FileText, Send, ClipboardList, CheckCircle2, Calendar, CalendarPlus, ChevronRight, Mail, MailCheck, AlertCircle, Stamp } from 'lucide-react';
import api from '../api/client';
import PanneauDossier from '../components/PanneauDossier';
import PanneauFinancement from '../components/PanneauFinancement';
import { messageErreur } from '../lib/erreur';
import SessionFormDialog from '../components/SessionFormDialog';
import AmendmentDialog from '../components/AmendmentDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import DetailSheet from '../components/DetailSheet';
import RowActions from '../components/RowActions';
import EmptyState from '../components/EmptyState';
import { SkeletonRow } from '../components/Skeleton';
import { exportToCsv, downloadPdf } from '../utils/export';
import InlineStatus from '../components/InlineStatus';
import { toast } from '../hooks/use-toast';
import { useLastSeen, useAutoMarkSeen } from '../hooks/useLastSeen';

const STATUS_STYLES = {
  'Confirmée': 'bg-[#E6FBF7] text-[#0F766E]',
  'Complète': 'bg-[#EEF2FF] text-[#4F46E5]',
  'En cours': 'bg-[#FEF3E7] text-[#B45309]',
  'Préinscription': 'bg-[#EEF2FF] text-[#4F46E5]',
  'Ouverte': 'bg-[#E0F2FE] text-[#0369A1]',
  'Terminée': 'bg-[#F3F4F8] text-[#4A4B63]',
};

const MONTHS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'];

export default function Sessions() {
  useAutoMarkSeen('sessions_active', 3000);
  const { isNew, markSeen } = useLastSeen();
  // Locally dismissed NOUVEAU badges — persisted to localStorage so dismissal
  // survives reloads/navigation.
  // SAFE: stores ONLY non-sensitive UI state — array of session IDs the user
  // has dismissed (used to hide the "NOUVEAU" badge across reloads). No
  // credentials, tokens, or PII. Auth tokens live in sessionStorage (XSS
  // hardening, Vague 97.x).
  const LS_KEY = 'ifaap_sessions_seen_ids';
  const [locallySeen, setLocallySeen] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const markLocalSeen = (id) => setLocallySeen((prev) => {
    if (prev.has(id)) return prev;
    const n = new Set(prev); n.add(id);
    try { localStorage.setItem(LS_KEY, JSON.stringify(Array.from(n).slice(-500))); } catch (e) { console.warn("[silent]", e); }
    return n;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState('list');
  const [search, setSearch] = useState('');
  const [monthOffset, setMonthOffset] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [create, setCreate] = useState(false);
  const [createInitial, setCreateInitial] = useState(null);
  const [createPreSelectedLearners, setCreatePreSelectedLearners] = useState([]);
  const [createPreinscription, setCreatePreinscription] = useState(false);
  const [createLockedFromQuote, setCreateLockedFromQuote] = useState(null); // null | { quoteNumber }
  const [createExistingSessionId, setCreateExistingSessionId] = useState(null); // when set, finalizing enrolls into this session instead of creating one
  const [createCompanyScope, setCreateCompanyScope] = useState(null); // { id, name } — restrict learner pool to this company
  const [editing, setEditing] = useState(null);
  const [amending, setAmending] = useState(null); // session being modified via amendment
  const [deleting, setDeleting] = useState(null);
  const [locks, setLocks] = useState({}); // { session_id: { locked, signed_count, total_count } }

  useEffect(() => {
    api.get('/sessions-locks').then(({ data }) => setLocks(data || {})).catch(() => {});
  }, [sessions]);
  const [detail, setDetail] = useState(null);
  const [detailLock, setDetailLock] = useState(null); // { locked, signed_count, total_count, signed_at }
  const [loading, setLoading] = useState(true);
  const [enrolled, setEnrolled] = useState([]);
  const [allLearners, setAllLearners] = useState([]);

  useEffect(() => {
    if (!detail?.id) { setDetailLock(null); return; }
    api.get(`/sessions/${detail.id}/conventions/list`).then(({ data }) => {
      setDetailLock({
        locked: !!data.locked,
        any_signed: !!data.any_signed,
        signed_count: data.signed_count || 0,
        total_count: data.total_count || 0,
      });
    }).catch(() => setDetailLock(null));
  }, [detail?.id]);

  useEffect(() => {
    api.get('/sessions').then(({ data }) => setSessions(data)).catch(() => {}).finally(() => setLoading(false));
    api.get('/learners').then(({ data }) => setAllLearners(data)).catch(() => {});
  }, []);

  // Open modal pre-filled from Devis conversion (router state)
  useEffect(() => {
    if (location.state?.fromDevis) {
      const { sessionPrefill, preSelectedLearnerIds, preinscription, lockedFromQuote, quoteNumber, existingSessionId, companyId, companyName } = location.state;
      setCreateInitial(sessionPrefill || {});
      setCreatePreSelectedLearners(preSelectedLearnerIds || []);
      setCreatePreinscription(!!preinscription);
      setCreateLockedFromQuote(lockedFromQuote ? { quoteNumber } : null);
      setCreateExistingSessionId(existingSessionId || null);
      setCreateCompanyScope(companyId ? { id: companyId, name: companyName || '' } : null);
      setCreate(true);
      // Clear the state so reopens are clean
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  useEffect(() => {
    if (detail) {
      api.get(`/sessions/${detail.id}/learners`).then(({ data }) => setEnrolled(data)).catch(() => setEnrolled([]));
    }
  }, [detail]);

  // Deep-link support — /app/sessions?focus=S-XXX opens that session's detail.
  useEffect(() => {
    if (!sessions.length) return;
    const params = new URLSearchParams(location.search || '');
    const sid = params.get('focus');
    if (!sid) return;
    const found = sessions.find((s) => s.id === sid);
    if (found) {
      setDetail(found);
      // Clean the URL so a reload doesn't re-trigger the drawer forever
      navigate(location.pathname, { replace: true });
    }
  }, [sessions, location.search, location.pathname, navigate]);

  const handleEnroll = async (learnerId) => {
    try {
      await api.post(`/sessions/${detail.id}/enroll`, { learner_id: learnerId });
      const { data } = await api.get(`/sessions/${detail.id}/learners`);
      setEnrolled(data);
      setSessions((p) => p.map((s) => s.id === detail.id ? { ...s, learners: (s.learners || 0) + 1 } : s));
      toast({ title: 'Apprenant inscrit' });
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Impossible d\u2019inscrire ce apprenant.' });
    }
  };

  const handleUnenroll = async (learnerId) => {
    try {
      await api.delete(`/sessions/${detail.id}/enroll/${learnerId}`);
      setEnrolled((prev) => prev.filter((l) => l.id !== learnerId));
      setSessions((p) => p.map((s) => s.id === detail.id ? { ...s, learners: Math.max(0, (s.learners || 0) - 1) } : s));
      toast({ title: 'Désinscrit' });
    } catch (e) { console.warn("[silent]", e); }
  };

  const handleCreated = async (s, enrolledCount, meta = {}) => {
    if (meta.enrolledOnly) {
      // Update existing row in place rather than prepending a new session
      setSessions((p) => p.map((x) => x.id === s.id ? s : x));
      toast({
        title: 'Pré-inscription finalisée ✅',
        description: `${enrolledCount} apprenant${enrolledCount > 1 ? 's' : ''} ajouté${enrolledCount > 1 ? 's' : ''} à ${s.title}${meta.skipped ? ` · ${meta.skipped} déjà inscrit(s)` : ''}.`,
      });
    } else {
      setSessions((p) => [s, ...p]);
      toast({ title: 'Session créée', description: enrolledCount ? `${s.title} — ${enrolledCount} apprenant(s) inscrit(s)` : s.title });
    }

    // When the session was created/enrolled from a validated quote, finalize
    // the pré-inscription: persist the reference on the client company and
    // generate the archive PDF — but DON'T auto-open it. The PDF remains
    // downloadable on demand via the "Pré-inscriptions" tab on the company.
    if (createLockedFromQuote) {
      try {
        await api.post(`/sessions/${s.id}/preinscription-finalize`, {
          quote_id: createLockedFromQuote.quoteNumber,
        });
        toast({
          title: 'Archive générée ✅',
          description: 'Le PDF de pré-inscription est stocké sur la fiche entreprise (onglet Pré-inscriptions).',
        });
      } catch (err) {
        toast({ title: 'PDF non généré', description: err?.response?.data?.detail || 'Erreur lors de la finalisation.' });
      }
    }
    setCreateLockedFromQuote(null);
    setCreateExistingSessionId(null);
  };
  const handleUpdated = (s) => {
    setSessions((p) => p.map((x) => x.id === s.id ? s : x));
    toast({ title: 'Session modifiée', description: s.title });
  };
  const handleDelete = async () => {
    await api.delete(`/sessions/${deleting.id}`);
    setSessions((p) => p.filter((s) => s.id !== deleting.id));
    toast({ title: 'Session supprimée' });
  };

  const openCreate = () => {
    setCreateInitial(null);
    setCreatePreSelectedLearners([]);
    setCreatePreinscription(false);
    setCreate(true);
  };

  const handleConvocation = async (s) => {
    try {
      await downloadPdf(api, `/sessions/${s.id}/convocation/pdf`, `convocation-${s.id}`);
      toast({ title: 'Convocation téléchargée', description: `${s.id}.pdf` });
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de générer la convocation.' });
    }
  };

  const filtered = useMemo(() => sessions.filter((s) =>
    s.title.toLowerCase().includes(search.toLowerCase()) ||
    s.trainer.toLowerCase().includes(search.toLowerCase()) ||
    s.id.toLowerCase().includes(search.toLowerCase())
  ), [search, sessions]);

  const baseYear = new Date().getFullYear(), baseMonth = new Date().getMonth();
  const displayDate = useMemo(() => new Date(baseYear, baseMonth + monthOffset, 1), [baseYear, baseMonth, monthOffset]);
  const daysInMonth = new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 0).getDate();
  const firstDayIdx = (new Date(displayDate.getFullYear(), displayDate.getMonth(), 1).getDay() + 6) % 7;

  const sessionsByDay = useMemo(() => {
    const m = {};
    sessions.forEach((s) => {
      const d = new Date(s.startDate);
      // Match by month only (ignore year) so demo/seasonal data appears in the calendar
      // across years. The list view still uses the exact date.
      if (d.getMonth() === displayDate.getMonth()) {
        (m[d.getDate()] = m[d.getDate()] || []).push(s);
      }
    });
    return m;
  }, [displayDate, sessions]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8E8FA5]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une session, un formateur…" className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-[#EFF0F6] text-[13.5px] focus:border-[#C7D2FE] outline-none" />
        </div>
        <div className="flex bg-white rounded-xl border border-[#EFF0F6] p-1">
          {[['list', List, 'Liste'], ['calendar', CalendarDays, 'Calendrier'], ['grid', Grid3x3, 'Grille']].map(([v, Ic, label]) => (
            <button key={v} onClick={() => setView(v)} className={`h-8 px-3 rounded-lg text-[12.5px] font-semibold flex items-center gap-1.5 transition-colors ${view === v ? 'bg-[#EEF2FF] text-[#4F46E5]' : 'text-[#6B6C85]'}`}>
              <Ic className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        <button onClick={() => exportToCsv('sessions', filtered, ['id','title','program','trainer','startDate','endDate','duration','mode','location','learners','capacity','status'])} className="h-10 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-medium text-[#4A4B63] flex items-center gap-2 hover:border-[#C7D2FE]">
          <Download className="w-4 h-4" /> Exporter
        </button>
        <button
          data-testid="sessions-export-ics"
          onClick={async () => {
            try {
              const res = await api.get('/calendar/sessions.ics', { responseType: 'blob' });
              const blob = new Blob([res.data], { type: 'text/calendar' });
              const href = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = href; a.download = 'sessions.ics';
              document.body.appendChild(a); a.click(); document.body.removeChild(a);
              URL.revokeObjectURL(href);
              toast({ title: 'Calendrier exporté', description: 'Importer dans Google / Outlook / Apple Calendar' });
            } catch { toast({ title: 'Erreur' }); }
          }}
          className="h-10 px-3.5 rounded-xl bg-white border border-[#EFF0F6] text-[13px] font-semibold text-[#4A4B63] flex items-center gap-2 hover:border-[#C7D2FE]"
        >
          📅 Exporter calendrier
        </button>
        <button onClick={openCreate} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center gap-2 shadow-sm hover:opacity-95">
          <Plus className="w-4 h-4" /> Nouvelle session
        </button>
      </div>

      {view === 'list' && (
        <div className="soft-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] border-b border-[#EFF0F6]">
                <th className="py-3.5 px-5">Réf.</th>
                <th className="py-3.5 px-5">Session</th>
                <th className="py-3.5 px-5">Date</th>
                <th className="py-3.5 px-5">Formateur</th>
                <th className="py-3.5 px-5">Mode</th>
                <th className="py-3.5 px-5">Apprenants</th>
                <th className="py-3.5 px-5">Statut</th>
                <th className="py-3.5 px-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={8} />)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8}><EmptyState icon={CalendarDays} title="Aucune session" description="Planifiez votre première session ou ajustez vos filtres." action={<button onClick={openCreate} className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold">Nouvelle session</button>} /></td></tr>
              )}
              {!loading && filtered.map((s) => {
                const itemIsNew = isNew('sessions_active', s.created_at) && !locallySeen.has(s.id);
                return (
                <tr key={s.id} onClick={() => { markLocalSeen(s.id); markSeen('sessions_active'); setDetail(s); }} className={`data-row border-b border-[#F3F4F8] last:border-0 cursor-pointer ${itemIsNew ? 'bg-[#FAF6FF] hover:bg-[#EEF2FF]' : ''}`}>
                  <td className="py-4 px-5 text-[12.5px] font-mono text-[#8E8FA5]">{s.id}</td>
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-2">
                      <span className="text-[13.5px] font-semibold text-[#1A1B2E]">{s.title}</span>
                      {itemIsNew && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#4F46E5] text-white text-[9.5px] font-bold tracking-wide">NOUVEAU</span>}
                      {locks[s.id]?.locked && (
                        <span data-testid={`session-lock-badge-${s.id}`} title={`Verrouillée — ${locks[s.id].signed_count}/${locks[s.id].total_count} conventions signées`} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-[#E6FBF7] text-[#0F766E] text-[10px] font-bold tracking-wide">
                          🔒 VERROUILLÉE
                        </span>
                      )}
                    </div>
                    <div className="text-[11.5px] text-[#8E8FA5]">{s.program} · {s.duration}h</div>
                  </td>
                  <td className="py-4 px-5 text-[13px] text-[#4A4B63]">{new Date(s.startDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                  <td className="py-4 px-5 text-[13px] text-[#4A4B63]">{s.trainer}</td>
                  <td className="py-4 px-5">
                    <span className="inline-flex items-center gap-1 text-[12px] text-[#4A4B63]">
                      {s.mode === 'Distanciel' ? <Video className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />} {s.mode}
                    </span>
                  </td>
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 max-w-[80px] h-1.5 bg-[#F3F4F8] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(s.learners / s.capacity) * 100}%`, background: s.learners === s.capacity ? '#14B8A6' : '#4F46E5' }} />
                      </div>
                      <span className="text-[12px] font-semibold text-[#4A4B63] whitespace-nowrap">{s.learners}/{s.capacity}</span>
                    </div>
                  </td>
                  <td className="py-4 px-5">
                    <InlineStatus
                      testId={`status-session-${s.id}`}
                      value={s.status}
                      options={['Préinscription', 'Ouverte', 'Confirmée', 'Complète', 'En cours', 'Terminée']}
                      styles={STATUS_STYLES}
                      onChange={async (newStatus) => {
                        const prev = s.status;
                        setSessions((list) => list.map((x) => x.id === s.id ? { ...x, status: newStatus } : x));
                        try {
                          await api.put(`/sessions/${s.id}`, { ...s, status: newStatus });
                          toast({ title: 'Statut modifié', description: `${s.title} → ${newStatus}` });
                        } catch (err) {
                          setSessions((list) => list.map((x) => x.id === s.id ? { ...x, status: prev } : x));
                          const detail = err?.response?.data?.detail;
                          if (detail && typeof detail === 'object' && Array.isArray(detail.errors)) {
                            toast({ title: detail.message || 'Incohérences bloquantes', description: detail.errors.slice(0, 3).join(' · ') });
                          } else {
                            toast({ title: 'Erreur', description: typeof detail === 'string' ? detail : 'Changement de statut refusé' });
                          }
                          throw new Error();
                        }
                      }}
                    />
                  </td>
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-1.5">
                      {(() => {
                        const isFull = (s.learners || 0) >= (s.capacity || 0);
                        return (
                          <button
                            type="button"
                            data-testid={`session-add-learner-${s.id}`}
                            onClick={(e) => { e.stopPropagation(); if (!isFull) { markLocalSeen(s.id); setDetail(s); } }}
                            disabled={isFull}
                            title={isFull ? 'Session complète — capacité atteinte' : "Ajouter un apprenant à cette session"}
                            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isFull
                              ? 'bg-[#F3F4F8] text-[#8E8FA5] cursor-not-allowed'
                              : 'hover:bg-[#EEF2FF] text-[#4F46E5]'}`}
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                          </button>
                        );
                      })()}
                      <SessionConventionAction s={s} onUpdate={(updated) => setSessions((list) => list.map((x) => x.id === s.id ? { ...x, ...updated } : x))} />
                      <SessionConvocationAction s={s} onUpdate={(updated) => setSessions((list) => list.map((x) => x.id === s.id ? { ...x, ...updated } : x))} />
                      <SessionQuestionnaireAction s={s} onUpdate={(updated) => setSessions((list) => list.map((x) => x.id === s.id ? { ...x, ...updated } : x))} />
                      <RowActions
                        onView={() => setDetail(s)}
                        onEdit={() => {
                          if (locks[s.id]?.locked) {
                            setAmending(s);
                            return;
                          }
                          setEditing(s);
                        }}
                        onDelete={() => {
                          if (locks[s.id]?.any_signed) {
                            toast({ title: 'Suppression bloquée', description: 'Au moins une convention liée est déjà signée — engagement contractuel.' });
                            return;
                          }
                          setDeleting(s);
                        }}
                        disableDelete={!!locks[s.id]?.any_signed}
                        disabledReason={locks[s.id]?.locked ? 'Session verrouillée — l\'édition ouvrira un avenant' : (locks[s.id]?.any_signed ? 'Au moins une convention est signée' : undefined)}
                      />
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {view === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <div key={s.id} onClick={() => setDetail(s)} className="soft-card p-5 cursor-pointer">
              <div className="flex items-start justify-between mb-3">
                <span className="text-[11px] font-mono text-[#8E8FA5]">{s.id}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_STYLES[s.status]}`}>{s.status}</span>
              </div>
              <h3 className="text-[15px] font-semibold text-[#1A1B2E] leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.title}</h3>
              <p className="text-[12px] text-[#8E8FA5] mt-0.5">{s.program}</p>
              <div className="mt-4 pt-3 border-t border-[#F3F4F8] space-y-1.5 text-[12.5px] text-[#4A4B63]">
                <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {new Date(s.startDate).toLocaleDateString('fr-FR')} · {s.duration}h</div>
                <div className="flex items-center gap-2">{s.mode === 'Distanciel' ? <Video className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />} {s.location}</div>
                <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {s.learners}/{s.capacity} · {s.trainer}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {view === 'calendar' && (
        <div className="soft-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setMonthOffset(monthOffset - 1)} className="w-8 h-8 rounded-lg border border-[#EFF0F6] flex items-center justify-center hover:bg-[#FAFAFE]"><ArrowLeft className="w-4 h-4" /></button>
              <h3 className="text-[17px] font-semibold text-[#1A1B2E] min-w-[180px] text-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{MONTHS[displayDate.getMonth()]} {displayDate.getFullYear()}</h3>
              <button onClick={() => setMonthOffset(monthOffset + 1)} className="w-8 h-8 rounded-lg border border-[#EFF0F6] flex items-center justify-center hover:bg-[#FAFAFE]"><ArrowRight className="w-4 h-4" /></button>
            </div>
            <button onClick={() => setMonthOffset(0)} className="h-8 px-3 text-[12px] font-semibold rounded-lg bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#EDE8FF]">Aujourd'hui</button>
          </div>
          <div className="grid grid-cols-7 rounded-xl overflow-hidden border border-[#EFF0F6]">
            {DAYS.map((d) => (
              <div key={d} className="bg-[#F8F8FB] py-2 text-center text-[11.5px] font-semibold uppercase text-[#8E8FA5] border-b border-[#EFF0F6]">{d}</div>
            ))}
            {Array.from({ length: firstDayIdx }).map((_, i) => (<div key={`e${i}`} className="cal-cell other-month" />))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const daySessions = sessionsByDay[day] || [];
              return (
                <div key={day} className="cal-cell">
                  <div className="text-[12px] font-semibold text-[#4A4B63] mb-1">{day}</div>
                  <div className="space-y-1">
                    {daySessions.slice(0, 3).map((s) => (
                      <button key={s.id} onClick={() => setDetail(s)} className="block w-full text-left text-[10.5px] font-medium px-1.5 py-1 rounded bg-[#EEF2FF] text-[#4F46E5] truncate hover:bg-[#EDE8FF]" title={s.title}>{s.title}</button>
                    ))}
                    {daySessions.length > 3 && <div className="text-[10.5px] text-[#8E8FA5]">+{daySessions.length - 3}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <SessionFormDialog
        open={create}
        onOpenChange={setCreate}
        mode="create"
        initialValues={createInitial}
        preSelectedLearnerIds={createPreSelectedLearners}
        preinscription={createPreinscription}
        lockedFromQuote={createLockedFromQuote}
        existingSessionId={createExistingSessionId}
        companyScope={createCompanyScope}
        onCreated={handleCreated}
      />
      <SessionFormDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        mode="edit"
        initialValues={editing}
        onUpdated={handleUpdated}
      />
      <AmendmentDialog
        session={amending}
        open={!!amending}
        onOpenChange={(o) => !o && setAmending(null)}
        onCreated={() => {
          // Refresh locks/sessions after amendment apply (status update happens on signing)
          api.get('/sessions').then(({ data }) => setSessions(data || [])).catch(() => {});
        }}
      />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Supprimer cette session ?" description={deleting?.title} onConfirm={handleDelete} />
      <DetailSheet
        open={!!detail}
        onOpenChange={(o) => !o && setDetail(null)}
        title={detail?.title}
        subtitle={detail?.program}
        badge={detail && <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_STYLES[detail.status]}`}>{detail.status}</span>}
        rows={detail ? [
          { label: 'Formateur', value: detail.trainer },
          (detail.date_ranges || []).filter((r) => r && r.startDate).length > 0
            ? {
                label: 'Périodes',
                value: [
                  `${new Date(detail.startDate).toLocaleDateString('fr-FR')} → ${detail.endDate ? new Date(detail.endDate).toLocaleDateString('fr-FR') : new Date(detail.startDate).toLocaleDateString('fr-FR')}`,
                  ...(detail.date_ranges || []).filter((r) => r && r.startDate).map((r) =>
                    `${new Date(r.startDate).toLocaleDateString('fr-FR')} → ${r.endDate ? new Date(r.endDate).toLocaleDateString('fr-FR') : new Date(r.startDate).toLocaleDateString('fr-FR')}`
                  ),
                ].join(' · '),
              }
            : null,
          (detail.date_ranges || []).filter((r) => r && r.startDate).length === 0
            ? { label: 'Début', value: new Date(detail.startDate).toLocaleDateString('fr-FR') }
            : null,
          (detail.date_ranges || []).filter((r) => r && r.startDate).length === 0
            ? { label: 'Fin', value: new Date(detail.endDate).toLocaleDateString('fr-FR') }
            : null,
          { label: 'Durée', value: `${detail.duration} h` },
          { label: 'Mode', value: detail.mode },
          { label: 'Lieu', value: detail.location },
          { label: 'Effectif', value: `${detail.learners} / ${detail.capacity}` },
        ].filter(Boolean) : []}
      >
        {detail && (
          <div className="mt-2 space-y-2">
            {detailLock?.locked && (
              <div data-testid="session-lock-banner" className="rounded-xl border-2 border-[#0F766E] bg-[#E6FBF7] p-3 flex items-start gap-2.5">
                <span className="text-[18px] leading-none">🔒</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] font-bold text-[#0F766E]">Session verrouillée — engagement contractuel</div>
                  <div className="text-[11.5px] text-[#0F766E]/85 mt-0.5">
                    Les {detailLock.signed_count} convention(s) liée(s) à cette session sont toutes signées. Pour toute modification (date, lieu, durée…), créez un <strong>avenant</strong> qui sera signé par le client.
                  </div>
                </div>
              </div>
            )}
            <SessionStepper session={detail} />
            <SessionHoraires sessionId={detail.id} />
            <PanneauFinancement sessionId={detail.id} />
            <PanneauDossier sessionId={detail.id} />
            {/* Primary action */}
            <button
              onClick={() => handleConvocation(detail)}
              className="w-full h-11 rounded-xl brand-gradient text-white text-[13.5px] font-semibold flex items-center justify-center gap-2 shadow-sm hover:opacity-95 transition-opacity"
            >
              <FileDown className="w-4 h-4" /> Télécharger la convocation (PDF)
            </button>

            <SessionConventionsList sessionId={detail.id} />

            {/* Secondary actions — 2×2 icon grid */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              {[
                {
                  key: 'ics', icon: Calendar, label: 'Télécharger .ics', testId: undefined,
                  onClick: async () => {
                    try {
                      const res = await api.get(`/sessions/${detail.id}/calendar.ics`, { responseType: 'blob' });
                      const blob = new Blob([res.data], { type: 'text/calendar' });
                      const href = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = href; a.download = `${detail.id}.ics`;
                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                      URL.revokeObjectURL(href);
                      toast({ title: 'Fichier .ics téléchargé' });
                    } catch { toast({ title: 'Erreur' }); }
                  }
                },
                {
                  key: 'gcal', icon: CalendarPlus, label: 'Google Calendar', testId: `gcal-${detail.id}`,
                  onClick: async () => {
                    try {
                      const { data } = await api.get(`/sessions/${detail.id}/gcal-link`);
                      window.open(data.url, '_blank');
                    } catch { toast({ title: 'Erreur' }); }
                  }
                },
                {
                  key: 'eval', icon: ClipboardList, label: "Demande d'évaluation", testId: `eval-${detail.id}`,
                  onClick: async () => {
                    try {
                      const { data } = await api.post(`/sessions/${detail.id}/send-evaluation-request`);
                      toast({ title: 'Questionnaire envoyé', description: `${data.count} apprenant(s) contacté(s).` });
                    } catch { toast({ title: 'Erreur' }); }
                  }
                },
                {
                  key: 'edit', icon: undefined, label: detailLock?.locked ? 'Créer un avenant 🔒' : 'Modifier la session', testId: `edit-session-${detail.id}`,
                  onClick: () => {
                    if (detailLock?.locked) {
                      setAmending(detail);
                      setDetail(null);
                      return;
                    }
                    setEditing(detail); setDetail(null);
                  }
                },
              ].map((act) => (
                <button
                  key={act.key}
                  data-testid={act.testId}
                  onClick={act.onClick}
                  className={`h-10 rounded-xl bg-white border text-[12.5px] font-semibold transition-colors flex items-center justify-center gap-1.5 ${act.key === 'edit' && detailLock?.locked ? 'border-[#FCD9A6] text-[#B45309] hover:bg-[#FEF3E7]' : 'border-[#EFF0F6] text-[#4A4B63] hover:border-[#C7D2FE] hover:bg-[#FAFAFE] hover:text-[#4F46E5]'}`}
                >
                  {act.icon && <act.icon className="w-3.5 h-3.5" />}
                  {act.label}
                </button>
              ))}
            </div>

            <SessionQuestionnaireResponses sessionId={detail.id} />

            {/* Enrolled learners — section card */}
            <div className="mt-5 rounded-xl border border-[#EFF0F6] overflow-hidden">
              <div className="px-4 py-3 bg-[#FAFAFE] border-b border-[#EFF0F6] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-3.5 h-3.5 text-[#4F46E5]" />
                  <h4 className="text-[12.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Apprenants inscrits</h4>
                  <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-white text-[#4F46E5] border border-[#C7D2FE]">
                    {enrolled.length} / {detail.capacity || '∞'}
                  </span>
                </div>
              </div>
              <div className="p-2 max-h-[220px] overflow-y-auto space-y-1">
                {enrolled.length === 0 && (
                  <div className="text-[12px] text-[#8E8FA5] italic text-center py-4">Aucun apprenant inscrit pour le moment.</div>
                )}
                {enrolled.map((l) => (
                  <div key={l.id} className="group flex items-center gap-2.5 p-2 rounded-lg hover:bg-[#EEF2FF] transition-colors">
                    <div className="w-8 h-8 rounded-full brand-gradient text-white font-semibold flex items-center justify-center text-[11px] shrink-0">
                      {l.firstName?.[0]}{l.lastName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{l.firstName} {l.lastName}</div>
                      <div className="text-[11px] text-[#8E8FA5] truncate">{l.company || '—'}</div>
                    </div>
                    <button
                      onClick={() => handleUnenroll(l.id)}
                      title="Désinscrire"
                      className="w-7 h-7 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-[#FEF2F2] text-[#EF4444] flex items-center justify-center transition-all"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <details className="border-t border-[#EFF0F6] group">
                <summary className="cursor-pointer px-4 py-2.5 text-[12.5px] font-semibold text-[#4F46E5] flex items-center justify-center gap-1.5 hover:bg-[#EEF2FF] transition-colors list-none">
                  <UserPlus className="w-3.5 h-3.5" /> Inscrire un apprenant
                </summary>
                <div className="max-h-[200px] overflow-y-auto p-2 space-y-0.5 border-t border-[#EFF0F6] bg-[#FAFAFE]">
                  {allLearners.filter((l) => !enrolled.find((e) => e.id === l.id)).length === 0 && (
                    <div className="text-[11.5px] text-[#8E8FA5] italic text-center py-2">Tous les apprenants sont déjà inscrits.</div>
                  )}
                  {allLearners.filter((l) => !enrolled.find((e) => e.id === l.id)).map((l) => (
                    <button
                      key={l.id}
                      onClick={() => handleEnroll(l.id)}
                      className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-white text-[12.5px] flex items-center gap-2 transition-colors"
                    >
                      <span className="font-medium text-[#1A1B2E]">{l.firstName} {l.lastName}</span>
                      {l.company && <span className="text-[11px] text-[#8E8FA5] truncate">· {l.company}</span>}
                    </button>
                  ))}
                </div>
              </details>
            </div>
          </div>
        )}
      </DetailSheet>
    </div>
  );
}


// =============================================================================
// Row action icons — Convention / Convocation / Questionnaire
// =============================================================================
function SessionConventionAction({ s, onUpdate }) {
  const [busy, setBusy] = React.useState(false);
  const isSigned = !!s.convention_signed_at;
  const isSent = !!s.convention_sign_token;
  const handleClick = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (isSigned) {
        await downloadPdf(api, `/sessions/${s.id}/convention/pdf`, `convention-${s.id}-signed`);
        toast({ title: 'Convention signée téléchargée' });
      } else {
        const { data } = await api.post(`/sessions/${s.id}/convention/signature-link`);
        // Vague 98.5 — aggregate per-company response
        const results = data.results || [];
        const total = data.total ?? results.length;
        const sent = data.sent ?? results.filter((r) => r.email_sent).length;
        const failures = results.filter((r) => !r.email_sent && r.email_error).map((r) => `${r.company_name || r.client || '—'}: ${r.email_error}`);
        if (total === 0) {
          toast({ title: 'Aucune convention à envoyer', description: 'Cette session n\'a aucun devis lié à une entreprise.' });
        } else if (sent === total) {
          const summary = results.length > 1
            ? `${sent} convention${sent > 1 ? 's' : ''} envoyée${sent > 1 ? 's' : ''} — 1 par entreprise`
            : `Lien transmis à ${results[0]?.email_to || 'l\'entreprise'}`;
          toast({ title: 'Convention(s) envoyée(s)', description: summary });
        } else if (sent > 0) {
          toast({ title: 'Envoi partiel', description: `${sent}/${total} envoyée(s) · ${failures.length ? failures.join(' · ') : 'voir détails'}` });
        } else {
          toast({ title: 'Envoi échoué', description: failures[0] || 'Configurez votre boîte email (Paramètres > Emails).' });
        }
        // Copy the first link as a fallback
        const firstLink = results.find((r) => r.full_url)?.full_url || data.full_url;
        if (firstLink) { try { await navigator.clipboard.writeText(firstLink); } catch (e) { console.warn("[silent]", e); } }
        onUpdate && onUpdate({ convention_sign_token: data.token, convention_email_sent_to: results[0]?.email_to || data.email_to });
      }
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Action impossible' });
    }
    setBusy(false);
  };
  const tone = isSigned
    ? 'bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC]'
    : isSent
      ? 'bg-[#FFF7E6] text-[#B45309] hover:bg-[#FCE7B3]'
      : 'hover:bg-[#EEF2FF] text-[#4F46E5]';
  const title = isSigned
    ? `Convention signée par ${s.convention_signatory_name || 'le client'} — clic pour télécharger`
    : isSent
      ? "Convention envoyée — en attente de signature"
      : "Envoyer la convention pour signature";
  return (
    <button
      type="button"
      data-testid={`session-action-convention-${s.id}`}
      onClick={handleClick}
      disabled={busy}
      title={title}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-60 ${tone}`}
    >
      <FileText className="w-3.5 h-3.5" />
    </button>
  );
}

function SessionConvocationAction({ s, onUpdate }) {
  const [busy, setBusy] = React.useState(false);
  const isSent = !!s.convocation_sent_at;
  const handleClick = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${s.id}/convocation/send`);
      onUpdate && onUpdate({ convocation_sent_at: data.convocation_sent_at });
      const sent = (data.sent || []).length;
      const errs = (data.errors || []).length;
      if (sent > 0 && errs === 0) toast({ title: 'Convocations envoyées', description: `${sent} email${sent > 1 ? 's' : ''} expédié${sent > 1 ? 's' : ''}` });
      else if (sent > 0 && errs > 0) toast({ title: 'Envoi partiel', description: `${sent} OK · ${errs} en erreur` });
      else toast({ title: 'Aucun envoi', description: errs ? 'Toutes les tentatives ont échoué' : 'Aucun apprenant inscrit' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Envoi impossible' });
    }
    setBusy(false);
  };
  const tone = isSent ? 'bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC]' : 'hover:bg-[#EEF2FF] text-[#4F46E5]';
  const title = isSent
    ? `Convocations envoyées le ${new Date(s.convocation_sent_at).toLocaleDateString('fr-FR')} — clic pour renvoyer`
    : "Envoyer la convocation aux apprenants (avec livret d'accueil)";
  return (
    <button
      type="button"
      data-testid={`session-action-convocation-${s.id}`}
      onClick={handleClick}
      disabled={busy}
      title={title}
      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-60 ${tone}`}
    >
      <Send className="w-3.5 h-3.5" />
    </button>
  );
}

function SessionQuestionnaireAction({ s, onUpdate }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const isSent = !!s.positioning_sent_at;
  // Les étapes viennent du référentiel : elles étaient écrites en dur, et les
  // cinq autres n'étaient envoyables depuis nulle part.
  const [types, setTypes] = React.useState(null);
  const [adresse, setAdresse] = React.useState('');
  React.useEffect(() => {
    if (!open || types) return;
    api.get('/types-questionnaire')
      .then(({ data }) => setTypes(data.types || []))
      .catch(() => setTypes([]));
  }, [open, types]);

  const sendKind = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${s.id}/questionnaire/send`,
        { kind, email: adresse.trim() || undefined });
      const sentCount = (data?.sent || []).length;
      const errCount = (data?.errors || []).length;
      const stamp = new Date().toISOString();
      const patch = kind === 'satisfaction'
        ? { satisfaction_sent_at: stamp }
        : { positioning_sent_at: stamp };
      onUpdate && onUpdate(patch);
      if (sentCount === 0) {
        toast({ title: 'Aucun envoi', description: errCount > 0 ? `${errCount} erreur(s) — vérifiez les emails ou créez le questionnaire.` : 'Aucun apprenant inscrit.' });
      } else {
        toast({ title: 'Questionnaire envoyé', description: `${sentCount} destinataire(s) contacté(s)${errCount ? ` · ${errCount} erreur(s)` : ''}.` });
      }
    } catch (err) {
      toast({ title: "L'envoi n'a pas eu lieu",
              description: err?.response?.data?.detail || 'Envoi impossible',
              variant: 'destructive' });
    }
    setBusy(false);
    setOpen(false);
  };
  const tone = isSent ? 'bg-[#E6FBF7] text-[#0F766E] hover:bg-[#CCF5EC]' : 'hover:bg-[#EEF2FF] text-[#4F46E5]';
  const btnRef = React.useRef(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const reposition = () => {
      const r = btnRef.current.getBoundingClientRect();
      const panelH = 132;
      const placeAbove = window.innerHeight - r.bottom < panelH + 8 && r.top > panelH + 8;
      setPos({ top: placeAbove ? r.top - panelH - 4 : r.bottom + 4, left: Math.max(8, r.right - 288) });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid={`session-action-questionnaire-${s.id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        disabled={busy}
        title={isSent ? 'Questionnaire envoyé — cliquez pour ré-envoyer ou choisir une autre étape' : 'Envoyer un questionnaire'}
        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors disabled:opacity-60 ${tone}`}
      >
        <ClipboardList className="w-3.5 h-3.5" />
      </button>
      {open && createPortal((
        <>
          <div className="fixed inset-0 z-[150]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div data-testid={`questionnaire-popover-${s.id}`} className="fixed z-[160] w-72 bg-white rounded-xl shadow-lg border border-[#EFF0F6] p-2" style={{ top: pos.top, left: pos.left }} onClick={(e) => e.stopPropagation()}>
            <div className="px-2.5 py-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-[#8E8FA5]">Envoyer maintenant</div>
            {!types && <div className="px-2.5 py-2 text-[12px] text-[#8E8FA5]">Chargement…</div>}
            <div className="max-h-64 overflow-y-auto">
              {(types || []).filter((t) => t.code !== 'other').map((t) => (
                <button
                  key={t.code}
                  data-testid={`questionnaire-send-${t.code}-${s.id}`}
                  onClick={() => sendKind(t.code)} disabled={busy}
                  className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-[#EEF2FF] text-[12.5px] text-[#1A1B2E] flex items-start gap-2 disabled:opacity-60"
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${
                    t.envoi_auto ? 'bg-[#0F766E]' : 'bg-[#C7CAD9]'}`} />
                  <span className="min-w-0">
                    <span className="block truncate">{t.libelle}</span>
                    <span className="block text-[10.5px] text-[#8E8FA5]">
                      {{
                        apprenant: 'aux apprenants inscrits',
                        formateur: 'au formateur',
                        entreprise: 'au contact de l’entreprise',
                        financeur: 'au contact du financeur',
                        libre: 'à l’adresse saisie ci-dessous',
                      }[t.destinataire] || t.destinataire}
                      {t.envoi_auto ? ' · part aussi tout seul' : ''}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            <div className="px-2.5 pt-2 pb-1 border-t border-[#F0F1F6] mt-1">
              <input
                value={adresse} onChange={(e) => setAdresse(e.target.value)}
                type="email" placeholder="ou à cette adresse précise…"
                data-testid={`questionnaire-adresse-${s.id}`}
                className="w-full h-8 px-2 rounded-lg border border-[#EFF0F6] text-[12px]"
              />
              <div className="text-[10.5px] text-[#8E8FA5] mt-1">
                Une adresse saisie l&rsquo;emporte sur la fiche. Les moments se
                règlent dans Paramètres › Questionnaires.
              </div>
            </div>
          </div>
        </>
      ), document.body)}
    </>
  );
}

function SessionQuestionnaireResponses({ sessionId }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [viewing, setViewing] = React.useState(null); // { invite, response, questionnaire }

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sessions/${sessionId}/questionnaire/responses`);
      setData(data);
    } catch (e) { console.warn("[silent]", e); }
    setLoading(false);
  }, [sessionId]);
  React.useEffect(() => { refresh(); }, [refresh]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/sessions/${sessionId}/questionnaire/responses.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `reponses-${sessionId}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Aucune réponse à exporter' });
    }
  };

  const openResponse = (inv) => {
    if (!inv.submitted_at) return;
    const resp = (data?.responses || []).find((r) => r.invite_token === inv.token || r.id === inv.response_id);
    if (!resp) {
      toast({ title: 'Réponses introuvables' });
      return;
    }
    const q = (data?.questionnaires || []).find((qq) => qq.id === resp.questionnaire_id) || (data?.questionnaires || [])[0];
    setViewing({ invite: inv, response: resp, questionnaire: q });
  };

  const sent = data?.sent || 0;
  const received = data?.received || 0;
  const allReceived = sent > 0 && received >= sent;

  if (loading && !data) return null;
  if (!data || sent === 0) return null;

  return (
    <div className="mt-5 rounded-xl border border-[#EFF0F6] overflow-hidden" data-testid={`session-questionnaire-responses-${sessionId}`}>
      <div className="px-4 py-3 bg-[#FAFAFE] border-b border-[#EFF0F6] flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-3.5 h-3.5 text-[#4F46E5]" />
          <h4 className="text-[12.5px] font-semibold text-[#1A1B2E]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Réponses aux questionnaires</h4>
          <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded border ${allReceived ? 'bg-[#E6FBF7] text-[#0F766E] border-[#A7F3D0]' : 'bg-white text-[#4F46E5] border-[#C7D2FE]'}`}>
            {received} / {sent}
          </span>
        </div>
        <button
          data-testid={`questionnaire-export-csv-${sessionId}`}
          onClick={exportCsv}
          disabled={received === 0}
          className="text-[11.5px] font-semibold text-[#4F46E5] hover:underline disabled:opacity-50 flex items-center gap-1"
        >
          <Download className="w-3 h-3" /> Export CSV
        </button>
      </div>
      <div className="p-2 max-h-[180px] overflow-y-auto">
        {(data.invites || []).map((inv) => (
          <button
            key={inv.id || inv.token}
            type="button"
            data-testid={`questionnaire-response-row-${inv.token}`}
            onClick={() => openResponse(inv)}
            disabled={!inv.submitted_at}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[12px] text-left transition-colors ${inv.submitted_at ? 'cursor-pointer hover:bg-[#EEF2FF]' : 'cursor-default opacity-80'}`}
          >
            <span className={`w-2 h-2 rounded-full ${inv.submitted_at ? 'bg-[#0F766E]' : 'bg-[#FCD34D]'}`} />
            <span className="flex-1 truncate text-[#1A1B2E]">{inv.learner_name || inv.learner_email}</span>
            <span className="text-[10.5px] text-[#8E8FA5]">{inv.submitted_at ? `Le ${new Date(inv.submitted_at).toLocaleDateString('fr-FR')}` : 'En attente'}</span>
            {inv.submitted_at && <ChevronRight className="w-3 h-3 text-[#4F46E5]" />}
          </button>
        ))}
      </div>
      {viewing && <QuestionnaireResponseModal data={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function QuestionnaireResponseModal({ data, onClose }) {
  const { invite, response, questionnaire } = data;
  const renderAnswer = (q) => {
    const v = response.answers?.[q.id];
    if (v === undefined || v === null || v === '') return <span className="italic text-[#8E8FA5]">— sans réponse</span>;
    if (Array.isArray(v)) return v.map((x) => <span key={x} className="inline-block mr-1 mb-1 px-2 py-0.5 rounded bg-[#EEF2FF] text-[#4F46E5] text-[11px] font-semibold">{x}</span>);
    if (q.type === 'scale') return <span className="inline-flex items-center gap-1 text-[14px] font-bold text-[#4F46E5]">{v} <span className="text-[10px] text-[#8E8FA5] font-normal">/ {q.scale_max ?? 5}</span></span>;
    return <span className="text-[12.5px] text-[#1A1B2E] whitespace-pre-wrap">{String(v)}</span>;
  };
  const questions = questionnaire?.questions || [];
  return createPortal((
    <div className="fixed inset-0 z-[200] bg-[#1A1B2E]/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="questionnaire-response-modal">
        <div className="px-6 py-4 border-b border-[#EFF0F6] flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-[15px] font-semibold text-[#1A1B2E]">{invite.learner_name || invite.learner_email}</h3>
            <p className="text-[11.5px] text-[#6B6C85] mt-0.5">
              {questionnaire?.title || 'Questionnaire'}
              {response.submitted_at && ` · répondu le ${new Date(response.submitted_at).toLocaleString('fr-FR')}`}
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[#F6F7FB] flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1 min-h-0">
          {questions.length === 0 && <div className="text-center text-[12.5px] text-[#8E8FA5] py-8 italic">Définition du questionnaire indisponible.</div>}
          {questions.map((q, i) => (
            <div key={q.id} className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3.5">
              <div className="text-[11.5px] font-semibold text-[#4F46E5] uppercase tracking-wide mb-1">Question {i + 1}{q.required ? ' *' : ''}</div>
              <div className="text-[13px] font-semibold text-[#1A1B2E] mb-2">{q.label}</div>
              <div>{renderAnswer(q)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  ), document.body);
}


export function SessionStepper({ session }) {
  const steps = [
    { key: 'created', label: 'Session créée', done: true, ts: session.created_at },
    { key: 'convention_sent', label: 'Convention envoyée', done: !!session.convention_sign_token, ts: null },
    { key: 'convention_signed', label: 'Convention signée', done: !!session.convention_signed_at, ts: session.convention_signed_at },
    { key: 'convocation_sent', label: 'Convocation envoyée', done: !!session.convocation_sent_at, ts: session.convocation_sent_at },
    { key: 'questionnaire_sent', label: 'Questionnaire J-1', done: !!session.positioning_sent_at, ts: session.positioning_sent_at },
    { key: 'started', label: 'Session démarrée', done: ['En cours', 'Terminée'].includes(session.status), ts: null },
    { key: 'ended', label: 'Session terminée', done: session.status === 'Terminée', ts: null },
  ];
  return (
    <div data-testid="session-stepper" className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3.5 my-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6B6C85] mb-2.5">Suivi de la session</div>
      <ol className="space-y-1.5">
        {steps.map((st, i) => (
          <li key={st.key} className="flex items-start gap-2.5">
            <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${st.done ? 'bg-[#0F766E] text-white' : 'bg-[#EFF0F6] text-[#8E8FA5]'}`}>
              {st.done ? <CheckCircle2 className="w-3 h-3" /> : <span className="text-[8.5px] font-bold">{i + 1}</span>}
            </span>
            <div className="flex-1 min-w-0">
              <div className={`text-[12.5px] ${st.done ? 'font-semibold text-[#1A1B2E]' : 'text-[#6B6C85]'}`}>{st.label}</div>
              {st.ts && <div className="text-[10.5px] text-[#8E8FA5]">{new Date(st.ts).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}



// --- Les horaires d'une formation, journée par journée -----------------------
// Les séances sont la seule vérité des heures : la feuille d'émargement les
// imprime en direct, sans passer par le motif de la fiche. Cet écran les règle
// une par une, là où le champ de la fiche les règle toutes.
//
// Les routes existaient depuis le premier jour et aucun écran ne les appelait —
// le bloc renvoyait même vers « Émargements », où ce geste n'existe pas.

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const JOURS_FR = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

function jourEnClair(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${JOURS_FR[d.getDay()]} ${d.getDate()} ${MOIS_FR[d.getMonth()]}`;
}

function jourSuivant(iso) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

// Les journées dans l'ordre, chacune avec ses créneaux. Une demi-journée est une
// journée qui n'en porte qu'un : c'est la seule différence entre les deux.
function grouperParJour(seances) {
  const par = new Map();
  (seances || []).forEach((s) => {
    if (!par.has(s.date)) par.set(s.date, []);
    par.get(s.date).push(s);
  });
  return [...par.entries()]
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([date, cr]) => [date, [...cr].sort((x, y) => (x.debut < y.debut ? -1 : 1))]);
}

// Une journée réglée à part est celle dont un créneau ne correspond à aucun de
// ceux du motif : c'est exactement celle que le motif ne touchera jamais.
function suitLeMotif(creneaux, motif) {
  if (!motif || !motif.length) return true;
  return creneaux.every((s) => motif.some((c) => c.debut === s.debut && c.fin === s.fin));
}

function SessionHoraires({ sessionId }) {
  const [etat, setEtat] = React.useState(null); // null = pas encore lu
  const [occupe, setOccupe] = React.useState(false);

  const charger = React.useCallback(async () => {
    try {
      const { data } = await api.get(`/sessions/${sessionId}/seances`);
      setEtat(data || { seances: [] });
    } catch (e) { setEtat({ seances: [] }); }
  }, [sessionId]);
  React.useEffect(() => { charger(); }, [charger]);

  const seances = etat?.seances || [];
  const motif = etat?.motif || [];
  const defaut = etat?.horaire_par_defaut || [];
  const jours = grouperParJour(seances);
  const horsDates = etat?.hors_dates || [];
  const sansSeance = etat?.sans_seance || [];
  const dates = etat?.dates || {};

  const agir = async (promesse, succes) => {
    setOccupe(true);
    try {
      await promesse;
      await charger();
      if (succes) toast({ title: succes });
    } catch (e) {
      toast({
        title: 'Refusé',
        description: e?.response?.data?.detail || 'Impossible pour le moment',
      });
    }
    setOccupe(false);
  };

  const engendrer = () => agir(
    api.post(`/sessions/${sessionId}/seances/generer`, {}),
    'Horaires posés sur chaque journée',
  );

  const changerHeure = (s, champ, valeur) => {
    if (!valeur || valeur === s[champ]) return;
    agir(api.put(`/seances/${s.id}`, {
      date: s.date, debut: s.debut, fin: s.fin, [champ]: valeur,
    }));
  };

  const retirer = (s) => agir(api.delete(`/seances/${s.id}`));

  // Le serveur refait les journees aux dates de la formation, avec le motif —
  // et refuse si une signature a deja ete recueillie : une preuve de presence
  // ne s'efface pas par un effet de bord.
  const reposer = () => agir(
    api.post(`/sessions/${sessionId}/seances/generer`, { remplacer: true }),
    'Journées reposées sur les dates de la formation',
  );

  // Les journées de la formation qui n'ont encore aucune séance — la seconde
  // période d'une formation non consécutive, ajoutée après coup. Le serveur
  // n'empile pas de doublon : les journées déjà posées ne bougent pas.
  const completer = () => agir(
    api.post(`/sessions/${sessionId}/seances/generer`, {}),
    'Séances posées sur les journées qui n’en avaient pas',
  );

  // Le créneau proposé est celui du motif que la journée ne porte pas encore :
  // un matin déjà là propose l'après-midi, et l'inverse.
  const ajouterDemiJournee = (date, creneaux) => {
    const modele = (motif.length ? motif : defaut);
    const libre = modele.find((c) => !creneaux.some((s) => s.debut === c.debut && s.fin === c.fin))
      || modele[modele.length - 1] || { debut: '14:00', fin: '17:00' };
    agir(api.post(`/sessions/${sessionId}/seances`, {
      date, debut: libre.debut, fin: libre.fin,
    }));
  };

  const ajouterJournee = async () => {
    const modele = (motif.length ? motif : defaut);
    if (!modele.length) return;
    const date = jours.length ? jourSuivant(jours[jours.length - 1][0])
      : new Date().toISOString().slice(0, 10);
    await agir((async () => {
      for (const c of modele) {
        await api.post(`/sessions/${sessionId}/seances`, { date, debut: c.debut, fin: c.fin });
      }
    })());
  };

  if (etat === null) return null;

  if (!seances.length) {
    // On nomme le geste qui le règle, et l'horaire qu'il posera : un bouton qui
    // écrit un horaire sans le montrer pose un horaire que personne n'a décidé —
    // et c'est le client qui le lirait.
    const propose = (motif.length ? motif : defaut)
      .map((c) => `${c.debut} - ${c.fin}`).join(' et ');
    return (
      <div data-testid="session-horaires-absents" className="rounded-xl border-2 border-[#F59E0B] bg-[#FFFBEB] p-3 flex items-start gap-2.5">
        <span className="text-[18px] leading-none">🕑</span>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-bold text-[#B45309]">Aucun horaire — la convocation partira sans</div>
          <div className="text-[11.5px] text-[#B45309]/90 mt-0.5">
            Les horaires viennent des séances de la formation. Rien n'est inventé : tant qu'aucune n'existe, le document ne porte pas d'horaire.
          </div>
          <button
            onClick={engendrer}
            disabled={occupe}
            className="mt-2 h-8 px-3 rounded-lg bg-[#B45309] text-white text-[11.5px] font-semibold disabled:opacity-60"
          >
            {occupe ? 'Un instant…' : `Poser ${propose || 'l’horaire type'} sur chaque journée`}
          </button>
          <div className="text-[11px] text-[#B45309]/80 mt-1.5">
            Chaque journée se règle ensuite ici, une par une.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="session-horaires" className="rounded-xl border border-[#EFF0F6] bg-[#FAFAFE] p-3">
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 mb-2">
        <span className="text-[11px] font-bold text-[#8E8FA5] uppercase tracking-wide">Horaires</span>
        <span className="text-[12px] text-[#5c5d70] tabular-nums">
          {jours.length} journée{jours.length > 1 ? 's' : ''} · {seances.length} séance{seances.length > 1 ? 's' : ''}
          {etat?.total_texte ? ` · ${etat.total_texte}` : ''}
        </span>
      </div>

      {horsDates.length > 0 && (
        <div className="rounded-lg border-2 border-[#F59E0B] bg-[#FFFBEB] p-2.5 mb-2">
          <div className="text-[12px] font-bold text-[#B45309]">
            {horsDates.length} journée{horsDates.length > 1 ? 's' : ''} hors des dates de la formation
          </div>
          <div className="text-[11.5px] text-[#B45309]/90 mt-0.5">
            {dates.debut ? `La formation est annoncée du ${jourEnClair(dates.debut)} au ${jourEnClair(dates.fin)}. ` : ''}
            Les documents annoncent ces dates-là et les horaires de ces journées-ci, et la
            feuille d'émargement — une page par séance — serait imprimée aux mauvais jours.
          </div>
          <button
            onClick={reposer} disabled={occupe}
            className="mt-2 h-8 px-3 rounded-lg bg-[#B45309] text-white text-[11.5px] font-semibold disabled:opacity-60"
          >
            {occupe ? 'Un instant…' : 'Reposer les journées sur les dates de la formation'}
          </button>
          <div className="text-[11px] text-[#B45309]/80 mt-1.5">
            Refusé si une signature a déjà été recueillie.
          </div>
        </div>
      )}

      {sansSeance.length > 0 && (
        <div data-testid="session-sans-seance" className="rounded-lg border-2 border-[#F59E0B] bg-[#FFFBEB] p-2.5 mb-2">
          <div className="text-[12px] font-bold text-[#B45309]">
            {sansSeance.length} journée{sansSeance.length > 1 ? 's' : ''} de la formation sans séance
          </div>
          <div className="text-[11.5px] text-[#B45309]/90 mt-0.5">
            {sansSeance.map(jourEnClair).join(', ')} — sans séance, ni horaire sur le devis et la
            convention, ni feuille d'émargement pour cette journée.
          </div>
          <button
            onClick={completer} disabled={occupe}
            className="mt-2 h-8 px-3 rounded-lg bg-[#B45309] text-white text-[11.5px] font-semibold disabled:opacity-60"
          >
            {occupe ? 'Un instant…' : 'Poser les séances manquantes'}
          </button>
        </div>
      )}

      {jours.map(([date, creneaux], i) => {
        const aPart = !suitLeMotif(creneaux, motif);
        return (
          <div key={date} className={i ? 'border-t border-[#EFF0F6] pt-2 mt-1' : ''}>
            <div className="flex items-center gap-2 flex-wrap text-[12px] font-semibold mb-1">
              <span>{jourEnClair(date)}</span>
              {creneaux.length === 1 && !aPart && (
                <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded-full bg-[#F5F2FF] text-[#7C5CFC]">
                  {creneaux[0].debut < '12:00' ? 'matin seul' : 'après-midi seul'}
                </span>
              )}
              {aPart && (
                <span className="text-[10.5px] font-bold px-1.5 py-0.5 rounded-full bg-[#FFFBEB] text-[#B45309] border border-[#F59E0B]">
                  réglée à part
                </span>
              )}
            </div>
            {creneaux.map((s) => (
              <div key={s.id} className="flex items-center gap-2 flex-wrap my-1">
                <input
                  type="time" defaultValue={s.debut} disabled={occupe}
                  onBlur={(e) => changerHeure(s, 'debut', e.target.value)}
                  className={`tabular-nums text-[13px] px-2 py-1 rounded-md border bg-white disabled:opacity-60 ${aPart ? 'border-[#F59E0B] text-[#B45309]' : 'border-[#EFF0F6]'}`}
                />
                <span className="text-[#8E8FA5]">–</span>
                <input
                  type="time" defaultValue={s.fin} disabled={occupe}
                  onBlur={(e) => changerHeure(s, 'fin', e.target.value)}
                  className={`tabular-nums text-[13px] px-2 py-1 rounded-md border bg-white disabled:opacity-60 ${aPart ? 'border-[#F59E0B] text-[#B45309]' : 'border-[#EFF0F6]'}`}
                />
                <span className="text-[11.5px] text-[#8E8FA5] tabular-nums">{s.duree_texte}</span>
                <button
                  onClick={() => retirer(s)} disabled={occupe}
                  title="Retirer cette demi-journée"
                  className="ml-auto text-[#8E8FA5] hover:text-[#B45309] text-[15px] leading-none px-1 disabled:opacity-40"
                >×</button>
              </div>
            ))}
            <button
              onClick={() => ajouterDemiJournee(date, creneaux)} disabled={occupe}
              className="text-[12px] font-semibold text-[#7C5CFC] disabled:opacity-50"
            >+ demi-journée</button>
          </div>
        );
      })}

      <div className="mt-2.5 pt-2 border-t border-[#EFF0F6] flex flex-wrap items-center gap-x-3 gap-y-1">
        <button
          onClick={ajouterJournee} disabled={occupe}
          className="text-[12px] font-semibold text-[#7C5CFC] disabled:opacity-50"
        >+ ajouter une journée</button>
        <span className="text-[11px] text-[#8E8FA5]">
          le motif de la formation suit quand toutes les journées ont le même horaire
        </span>
      </div>

      {(etat?.horaires || []).length > 0 && (
        <div className="mt-2 text-[11.5px] text-[#8E8FA5]">
          Sur la convocation : {etat.horaires.join(' · ')}
        </div>
      )}
    </div>
  );
}

function SessionConventionsList({ sessionId }) {
  const [items, setItems] = React.useState([]);
  // La convention portée par la formation elle-même : le chemin d'avant, quand
  // les conventions ne naissaient pas d'un devis. Une seule formation en porte
  // une, et sans ça son écran n'offrirait aucun geste.
  const [conventionFormation, setConventionFormation] = React.useState(null);
  const [counts, setCounts] = React.useState({ total: 0, sent: 0, signed: 0 });
  const [loading, setLoading] = React.useState(false);
  const [bulkBusy, setBulkBusy] = React.useState(false);
  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sessions/${sessionId}/conventions/list`);
      setItems(data?.items || []);
      setConventionFormation(data?.convention_formation || null);
      setCounts({
        total: data?.total_count || 0,
        sent: data?.sent_count || 0,
        signed: data?.signed_count || 0,
      });
    } catch (e) { console.warn("[silent]", e); }
    setLoading(false);
  }, [sessionId]);
  React.useEffect(() => { refresh(); }, [refresh]);

  const downloadOne = async (qid, signee) => {
    try {
      const res = await api.get(`/quotes/${qid}/convention/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      // Signee, le serveur rend la copie archivee au moment de la signature —
      // le nom du fichier le dit, sinon les deux se melangent dans un dossier.
      a.href = url; a.download = `convention-${qid}${signee ? '-signee' : ''}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'Erreur', description: 'Téléchargement impossible' }); }
  };
  // La contresignature de l'organisme. Elle part d'elle-meme avec l'envoi au
  // client ; ce bouton sert a la renvoyer, ou a la demander seule quand la
  // convention etait partie avant que ce chemin n'existe.
  const contresigner = async (qid) => {
    try {
      // Sans devis, le jeton se pose sur la formation : c'est la même demande,
      // par l'autre porte.
      const { data } = await api.post(qid
        ? `/quotes/${qid}/convention/contresignature-link`
        : `/sessions/${sessionId}/convention/contresignature-link`);
      if (data.email_sent) {
        toast({
          title: data.reused_token ? 'Lien de contresignature renvoyé' : 'Lien de contresignature envoyé',
          description: `À ${data.email_to}`,
        });
      } else {
        toast({
          title: 'Lien non envoyé',
          description: data.email_error || 'Aucune adresse sur la fiche de l\'organisme.',
          variant: 'destructive',
        });
      }
      refresh();
    } catch (e) {
      toast({
        title: 'Erreur',
        description: e?.response?.data?.detail || 'Envoi impossible',
        variant: 'destructive',
      });
    }
  };

  const sendOne = async (qid) => {
    try {
      const { data } = await api.post(`/quotes/${qid}/convention/signature-link`);
      // C'est le serveur qui décide du destinataire : la maison signe d'abord,
      // le client reçoit ensuite. L'écran dit lequel a reçu, sinon « Lien
      // envoyé » laisse croire que le client l'a.
      const pourOrganisme = data.destinataire === 'organisme';
      const action = pourOrganisme
        ? (data.reused_token ? 'Lien de contresignature renvoyé' : 'À contresigner par l\'organisme')
        : (data.reused_token ? 'Convention renvoyée au client' : 'Convention envoyée au client');
      if (data.email_sent) toast({ title: action, description: `À ${data.email_to}${data.email_sent_count > 1 ? ` (${data.email_sent_count}× au total)` : ''}` });
      else if (data.email_to) toast({ title: 'Lien généré', description: data.email_error || 'Email non envoyé — lien copié.' });
      else toast({ title: 'Lien généré', description: 'Aucun destinataire — copiez le lien manuellement.' });
      if (data.full_url) {
        try { await navigator.clipboard.writeText(data.full_url); } catch (e) { console.warn("[silent]", e); }
      }
      await refresh();
    } catch (e) {
      toast({ title: 'Erreur', description: messageErreur(e, 'Envoi impossible') });
    }
  };
  const sendAll = async () => {
    if (bulkBusy) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/convention/signature-link`);
      const total = data.total ?? 0;
      const sent = data.sent ?? 0;
      const vers = (data.destinataires || []).includes('organisme')
        ? ((data.destinataires || []).length > 1
            ? "à l'organisme et aux clients" : "à l'organisme, pour contresignature")
        : 'aux clients, 1 par entreprise';
      if (sent === total && total > 0) {
        toast({ title: `${sent} convention${sent > 1 ? 's' : ''} envoyée${sent > 1 ? 's' : ''}`, description: vers });
      } else if (sent > 0) {
        toast({ title: 'Envoi partiel', description: `${sent}/${total} envoyée(s)` });
      } else {
        toast({ title: 'Aucun envoi', description: 'Vérifiez les emails clients ou la config SMTP.' });
      }
      await refresh();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Envoi groupé impossible' });
    }
    setBulkBusy(false);
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  if (loading) return null;
  if (items.length === 0) {
    const cf = conventionFormation;
    return (
      <div className="rounded-xl border border-[#EFF0F6] overflow-hidden" data-testid={`session-conventions-empty-${sessionId}`}>
        {cf ? (
          <div className="px-3 py-2.5 flex items-center gap-2" data-testid={`session-convention-formation-${sessionId}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-semibold text-[#6B6C85]">Convention de la formation</span>
                {cf.signed_at && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#E6FBF7] text-[#0F766E]">
                    <CheckCircle2 className="w-2.5 h-2.5" /> Signée
                  </span>
                )}
              </div>
              <div className="text-[10.5px] text-[#6B6C85] truncate">
                {cf.signed_at
                  ? <>✓ {cf.signatory_name || 'le client'} · {fmtDate(cf.signed_at)}</>
                  : 'Signature du bénéficiaire en attente'}
              </div>
              <div className="text-[10.5px] truncate">
                {cf.contresignature?.signed_at ? (
                  <span className="text-[#4F46E5] font-semibold">
                    ✓ contresignée par {cf.contresignature.signatory_name || "l'organisme"} · {fmtDate(cf.contresignature.signed_at)}
                  </span>
                ) : cf.contresignature?.email_sent_at ? (
                  <span className="text-[#6B6C85]">
                    à contresigner — lien envoyé le {fmtDate(cf.contresignature.email_sent_at)}
                    {cf.contresignature.email_sent_to ? ` à ${cf.contresignature.email_sent_to}` : ''}
                  </span>
                ) : (
                  <span className="text-[#B8B9CC]">à contresigner par l'organisme</span>
                )}
              </div>
            </div>
            <button
              data-testid={`session-convention-formation-dl-${sessionId}`}
              onClick={() => downloadPdf(api, `/sessions/${sessionId}/convention/pdf`, `convention-${sessionId}`)}
              title="Télécharger la convention"
              className="w-8 h-8 rounded-lg hover:bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center"
            >
              <FileDown className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid={`session-convention-formation-contresign-${sessionId}`}
              onClick={() => contresigner(null)}
              disabled={!!cf.contresignature?.signed_at}
              title={cf.contresignature?.signed_at
                ? `Contresignée par ${cf.contresignature.signatory_name || "l'organisme"}`
                : "Envoyer le lien de contresignature au contact principal de l'organisme"}
              className={`h-8 px-2.5 rounded-lg text-[11.5px] font-semibold flex items-center gap-1 disabled:opacity-50 ${
                cf.contresignature?.signed_at
                  ? 'bg-[#EEF2FF] text-[#4F46E5]'
                  : 'bg-[#4F46E5] text-white hover:opacity-95'
              }`}
            >
              <Stamp className="w-3 h-3" />
              {cf.contresignature?.signed_at ? 'Contresignée' : 'Faire contresigner'}
            </button>
          </div>
        ) : (
          <div className="border border-dashed border-[#EFF0F6] bg-[#FAFAFE] p-3 text-center text-[12px] text-[#8E8FA5]">
            Aucun devis lié à cette session — la convention sera disponible après acceptation d'un devis.
          </div>
        )}
      </div>
    );
  }

  // State badge styles
  const STATE_BADGE = {
    signed: { label: 'Signée', cls: 'bg-[#E6FBF7] text-[#0F766E]', icon: CheckCircle2 },
    sent: { label: 'En attente', cls: 'bg-[#FEF3E7] text-[#B45309]', icon: Clock },
    ready: { label: 'Lien prêt', cls: 'bg-[#EEF2FF] text-[#4F46E5]', icon: FileText },
    expired: { label: 'Expiré', cls: 'bg-[#FEF2F2] text-[#B91C1C]', icon: AlertCircle },
    draft: { label: 'À envoyer', cls: 'bg-[#F3F4F8] text-[#4A4B63]', icon: Mail },
  };

  const allSigned = counts.total > 0 && counts.signed === counts.total;

  return (
    <div className="rounded-xl border border-[#EFF0F6] overflow-hidden" data-testid={`session-conventions-${sessionId}`}>
      <div className="px-3 py-2.5 bg-[#FAFAFE] border-b border-[#EFF0F6] flex items-center gap-2">
        <FileText className="w-3.5 h-3.5 text-[#0F766E]" />
        <h4 className="text-[12.5px] font-semibold text-[#1A1B2E]">État des signatures par société</h4>
        <span className="text-[10.5px] text-[#8E8FA5] ml-auto" data-testid={`session-conventions-counter-${sessionId}`}>
          {counts.signed}/{counts.total} signée{counts.total > 1 ? 's' : ''}
          {counts.sent > counts.signed ? ` · ${counts.sent - counts.signed} en attente` : ''}
        </span>
        {!allSigned && (
          <button
            data-testid={`session-conventions-sendall-${sessionId}`}
            onClick={sendAll}
            disabled={bulkBusy}
            className="h-7 px-2.5 rounded-lg bg-[#0F766E] text-white text-[11px] font-semibold hover:bg-[#0E6D66] flex items-center gap-1 disabled:opacity-50"
            title="Envoyer toutes les conventions non signées en une seule action"
          >
            <Send className="w-3 h-3" /> {bulkBusy ? 'Envoi…' : 'Tout envoyer'}
          </button>
        )}
      </div>
      <div className="divide-y divide-[#EFF0F6]">
        {items.map((it) => {
          const meta = STATE_BADGE[it.state] || STATE_BADGE.draft;
          const Icon = meta.icon;
          const tooltip = it.signed_at
            ? `Signée le ${fmtDate(it.signed_at)} par ${it.signatory_name || 'le client'}`
            : !it.contresignature?.signed_at
              ? "L'organisme signe d'abord — le lien de contresignature part à son contact principal, et la convention partira au client dès qu'il aura signé"
              : it.email_sent_at
              ? `Envoyée ${it.email_sent_count}× — Dernier envoi : ${fmtDate(it.email_sent_at)} à ${it.email_sent_to || '—'}`
              : it.contact_email
                ? `Cliquer pour envoyer à ${it.contact_email}`
                : 'Aucun email destinataire — copiez le lien manuellement';
          return (
            <div key={it.quote_id} className="px-3 py-2.5 flex items-center gap-2" data-testid={`session-convention-row-${it.quote_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] font-semibold text-[#4F46E5]">{it.quote_id}</span>
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${meta.cls}`}
                    data-testid={`session-convention-badge-${it.quote_id}`}
                  >
                    <Icon className="w-2.5 h-2.5" /> {meta.label}
                  </span>
                  {it.email_sent_count > 1 && !it.signed_at && (
                    <span className="text-[10px] text-[#8E8FA5]">· envoyé {it.email_sent_count}×</span>
                  )}
                </div>
                <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">
                  {it.company_name}
                  {it.learner_count > 0 && (
                    <span className="ml-1.5 text-[10.5px] font-normal text-[#6B6C85]">
                      · {it.learner_count} apprenant{it.learner_count > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="text-[10.5px] text-[#6B6C85] truncate">
                  {it.amount_ttc != null ? `${Number(it.amount_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} € TTC` : '—'}
                  {it.signed_at && <span className="ml-1.5 text-[#0F766E] font-semibold">✓ {it.signatory_name || 'le client'} · {fmtDate(it.signed_at)}</span>}
                  {!it.signed_at && it.email_sent_at && <span className="ml-1.5 text-[#B45309]">📨 {fmtDate(it.email_sent_at)}</span>}
                </div>
                {/* Et l'organisme : une convention signée d'un seul côté n'est
                    pas un acte complet, la ligne doit le dire. */}
                <div className="text-[10.5px] truncate">
                  {it.contresignature?.signed_at ? (
                    <span className="text-[#4F46E5] font-semibold">
                      ✓ contresignée par {it.contresignature.signatory_name || 'l\'organisme'} · {fmtDate(it.contresignature.signed_at)}
                    </span>
                  ) : it.contresignature?.email_sent_at ? (
                    <span className="text-[#6B6C85]">
                      à contresigner — lien envoyé le {fmtDate(it.contresignature.email_sent_at)}
                      {it.contresignature.email_sent_to ? ` à ${it.contresignature.email_sent_to}` : ''}
                    </span>
                  ) : (
                    <span className="text-[#B8B9CC]">à contresigner par l'organisme</span>
                  )}
                </div>
              </div>
              <button
                data-testid={`session-convention-dl-${it.quote_id}`}
                onClick={() => downloadOne(it.quote_id, !!it.signed_at)}
                title={it.signed_at
                  ? (it.copie_archivee
                      ? 'Télécharger la convention signée — la copie archivée au moment de la signature'
                      : 'Télécharger la convention signée — reconstituée depuis le registre des signatures')
                  : 'Télécharger la convention (brouillon, non signée)'}
                className="w-8 h-8 rounded-lg hover:bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center"
              >
                <FileDown className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`session-convention-contresign-${it.quote_id}`}
                onClick={() => contresigner(it.quote_id)}
                disabled={!!it.contresignature?.signed_at}
                title={it.contresignature?.signed_at
                  ? `Contresignée par ${it.contresignature.signatory_name || "l'organisme"} le ${fmtDate(it.contresignature.signed_at)}`
                  : it.contresignature?.email_sent_at
                    ? `Renvoyer le lien de contresignature à ${it.contresignature.email_sent_to || "l'organisme"}`
                    : "Envoyer le lien de contresignature au contact principal de l'organisme"}
                className={`w-8 h-8 rounded-lg flex items-center justify-center disabled:opacity-50 ${
                  it.contresignature?.signed_at
                    ? 'bg-[#EEF2FF] text-[#4F46E5]'
                    : 'hover:bg-[#EEF2FF] text-[#6B6C85]'
                }`}
              >
                <Stamp className="w-3.5 h-3.5" />
              </button>
              <button
                data-testid={`session-convention-sign-${it.quote_id}`}
                onClick={() => sendOne(it.quote_id)}
                title={tooltip}
                disabled={!!it.signed_at}
                className={`h-8 px-2.5 rounded-lg text-[11.5px] font-semibold flex items-center gap-1 disabled:opacity-50 ${
                  it.signed_at
                    ? 'bg-[#E6FBF7] text-[#0F766E]'
                    : it.email_sent_at
                      ? 'bg-[#FEF3E7] text-[#B45309] hover:bg-[#FDE68A]'
                      : 'bg-[#0F766E] text-white hover:bg-[#0E6D66]'
                }`}
              >
                {it.signed_at
                  ? <><CheckCircle2 className="w-3 h-3" /> Signée</>
                  : !it.contresignature?.signed_at
                    ? <><Stamp className="w-3 h-3" /> Faire contresigner</>
                    : it.email_sent_at
                      ? <><MailCheck className="w-3 h-3" /> Renvoyer</>
                      : <><Send className="w-3 h-3" /> Envoyer au client</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
